/**
 * Cruise lodging city picker + save target — mirrors iOS
 * CruiseLodgingCityVisitFilter + CruiseCityStayGrouping.lodgingSaveTarget.
 */

import type { Trip, TripHotelStay } from '../types/client';
import { isAtSeaLocation } from '../services/cruiseItinerariesApi';
import {
  cruiseCityStayContaining,
  cruiseCityStays,
  cruiseVisitRefsFromTrip,
  type CruiseCityVisitRef,
} from './cruiseCityStayGrouping';
import { stayArrivalDeparture } from './cityPlanningStay';

/** Open-water or transit rows that cannot host a hotel stay. */
export function isLodgingEligible(cityName: string): boolean {
  if (isAtSeaLocation(cityName)) return false;
  const n = cityName.toLowerCase().trim();
  return !n.includes('cruising');
}

export function isPreOrPostCruiseDay(
  trip: Trip,
  cityVisitId: string
): boolean {
  return trip.cityVisitMeta?.[cityVisitId]?.cruisePortStatus === 'prePost';
}

function cityVisitsForLodgingPicker(
  trip: Trip,
  showAllPorts: boolean
): CruiseCityVisitRef[] {
  const sorted = cruiseVisitRefsFromTrip(trip)
    .slice()
    .sort((a, b) => {
      if (a.arrivalDate && b.arrivalDate && a.arrivalDate !== b.arrivalDate) {
        return a.arrivalDate.localeCompare(b.arrivalDate);
      }
      return 0;
    })
    .filter((v) => isLodgingEligible(v.cityName));

  if (trip.tripType !== 'cruise') return sorted;
  if (showAllPorts) return sorted;
  return sorted.filter((v) => isPreOrPostCruiseDay(trip, v.id));
}

/** Eligible lodging cities collapsed to one anchor per consecutive same-city stay. */
export function lodgingPickerAnchors(
  trip: Trip,
  showAllPorts: boolean
): CruiseCityVisitRef[] {
  const eligible = cityVisitsForLodgingPicker(trip, showAllPorts);
  const result: CruiseCityVisitRef[] = [];
  const seen = new Set<string>();
  for (const visit of eligible) {
    const stay = cruiseCityStayContaining(trip, visit.id);
    const anchor = stay.visits[0];
    if (!anchor || seen.has(anchor.id)) continue;
    seen.add(anchor.id);
    result.push(anchor);
  }
  return result;
}

/** Hotel stays attached to any day in the consecutive same-city lodging group. */
export function lodgingHotelsForAnchor(
  trip: Trip,
  anchorId: string
): Array<TripHotelStay & { cityVisitId: string }> {
  const stay = cruiseCityStayContaining(trip, anchorId);
  const out: Array<TripHotelStay & { cityVisitId: string }> = [];
  for (const visit of stay.visits) {
    const hotels = trip.hotelStaysByCity?.[visit.id] ?? [];
    for (const h of hotels) {
      out.push({ ...h, cityVisitId: visit.id });
    }
  }
  return out.sort((a, b) => {
    const ai = a.checkInDate ?? '';
    const bi = b.checkInDate ?? '';
    if (ai !== bi) return ai.localeCompare(bi);
    return (a.checkOutDate ?? '').localeCompare(b.checkOutDate ?? '');
  });
}

/** Display string for existing lodging on a picker row (undefined when none). */
export function lodgingNamesLabel(hotels: TripHotelStay[]): string | undefined {
  const names = hotels
    .map((h) => h.hotelName.trim())
    .filter(Boolean);
  if (names.length === 0) return undefined;
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]}, ${names[1]}`;
  return `${names[0]} +${names.length - 1}`;
}

/**
 * Real cityVisitId to attach a hotel stay to.
 * Prefers the visit whose arrival day matches check-in; otherwise the stay anchor.
 */
export function lodgingSaveTargetCityVisitId(
  trip: Trip,
  selectedVisitId: string,
  checkInYmd: string
): string {
  const stay = cruiseCityStayContaining(trip, selectedVisitId);
  if (stay.visits.length === 0) return selectedVisitId;

  const checkIn = checkInYmd.trim().slice(0, 10);
  const byArrival = stay.visits.find((v) => v.arrivalDate === checkIn);
  if (byArrival) return byArrival.id;

  for (const visit of stay.visits) {
    const { arrivalDate, departureDate } = stayArrivalDeparture(trip, visit.id);
    if (arrivalDate && departureDate && checkIn >= arrivalDate && checkIn < departureDate) {
      return visit.id;
    }
  }

  return stay.visits[0].id;
}

export function defaultLodgingDateRange(
  trip: Trip,
  cityVisitId: string
): { checkIn: string; checkOut: string } {
  const { arrivalDate, departureDate } = stayArrivalDeparture(trip, cityVisitId);
  const checkIn = arrivalDate || trip.startDate || new Date().toISOString().slice(0, 10);
  let checkOut = departureDate || trip.endDate || checkIn;
  if (checkOut <= checkIn) {
    // Minimum one night
    const [y, m, d] = checkIn.split('-').map(Number);
    const next = new Date(Date.UTC(y, m - 1, d + 1));
    checkOut = next.toISOString().slice(0, 10);
  }
  return { checkIn, checkOut };
}

/** All stays on the trip flattened with cityVisitId, sorted by check-in. */
export function allTripHotelStays(
  trip: Trip
): Array<TripHotelStay & { cityVisitId: string; cityName: string }> {
  const names = trip.cityNames ?? {};
  const out: Array<TripHotelStay & { cityVisitId: string; cityName: string }> = [];
  for (const [cityVisitId, stays] of Object.entries(trip.hotelStaysByCity ?? {})) {
    for (const s of stays) {
      out.push({
        ...s,
        cityVisitId,
        cityName: names[cityVisitId]?.trim() || 'Unknown City',
      });
    }
  }
  return out.sort((a, b) => {
    const ai = a.checkInDate ?? '';
    const bi = b.checkInDate ?? '';
    if (ai !== bi) return ai.localeCompare(bi);
    return (a.checkOutDate ?? '').localeCompare(b.checkOutDate ?? '');
  });
}

/** Unique city count that currently have at least one hotel stay. */
export function lodgingCityCount(trip: Trip): number {
  return Object.values(trip.hotelStaysByCity ?? {}).filter((list) => list.length > 0).length;
}

/** Expose stay grouping for tests / callers that need full stay list. */
export function lodgingStays(trip: Trip) {
  return cruiseCityStays(trip);
}
